<template>

  <v-dialog class="issue-details-popup"
            v-if="visiblePopup"
            :value="visiblePopup"
            fullscreen
            @click:outside="onCloseClickButton"
            @keydown.esc="onCloseClickButton"
  >
    <v-card class="popup-wrapper">
      <div class="popup-container">
        <div class="main-part">
          <div class="header-line">
            <div class="header-upper-line">
              <div class="data-title security-risk-data-title">{{$t('issueDataPopup.securityRisk')}}</div>
              <div class="action-button-container mark-issue-as-fixed-button-container"
                   v-if="userRoleGroup === userGroups.CISO">
                <change-issue-status-button v-if="userRoleGroup === userGroups.CISO"
                                            @issueStatusChanged="issueStatusChangedHandler"
                                            :project-id="projectId"
                                            :issue="issueDetails"/>
              </div>
              <div class="action-button-container edit-delete-issue-button-container"
                   v-if="userRoleGroup === userGroups.SP">
                <edit-delete-issue-button class="edit-delete-issue-button"
                                          @deleteIssueClicked="deleteIssueClickedHandler"
                                          @editIssueClicked="editIssueClickedHandler"
                                          :issue="issueDetails"/>
              </div>
            </div>
            <div class="security-risk">
              <span class="risk-code">{{securityRiskCode}}</span>
              <div class="security-risk-description"
                   :title="securityRiskDescription">
                {{securityRiskDescription}}
              </div>
            </div>
            <div class="close"
                 @click="onCloseClickButton">
              X {{$t('newProject.esc')}}
            </div>
          </div>

          <span class="description-block">
            <div class="data-title">{{$t('issueDataPopup.description')}}</div>
            <div class="text-container">
              {{issueDetails.description}}
            </div>
          </span>
          <div class="data-row">
            <div class="data-column">
              <div class="data-title">{{$t('issueDataPopup.probability')}}</div>
              <issue-probability :issue="issueDetails"/>
            </div>
            <div class="data-column">
              <div class="data-title">{{$t('issueDataPopup.severity')}}</div>
              <issue-severity class="severity-risk-score-line" :issue="issueDetails"/>
              <issue-risk-score class="severity-risk-score-line issue-risk-score" :issue="issueDetails"/>
            </div>
            <div class="data-column">
              <div class="data-title">{{$t('issueDetailsPopup.status')}}</div>
              <issue-status class="issue-status" :issue="issueDetails"/>
            </div>
            <div class="data-column-date">
              <div class="data-title">{{$t('issueDetailsPopup.created')}}</div>
              <span class="date-column">
                {{createdDate}}
              </span>
            </div>
            <div class="data-column-date">
              <div class="data-title">{{$t('issueDetailsPopup.lastEdited')}}</div>
              <span class="date-column">
                {{updatedDate}}
              </span>
            </div>
          </div>
        </div>
        <div class="rest-part">
          <span class="comment-block">
            <div class="data-title">{{$t(commentsTitle)}}</div>
            <div class="text-container">
              {{issueDetails.comment}}
            </div>
          </span>
        </div>
      </div>
    </v-card>

    <yes-no-popup
      :visible-popup="showDeleteIssueConfirmationPopup"
      title="issue.delete.popup.title"
      message="issue.delete.popup.message"
      yesCaption="confirm.ok"
      noCaption="confirm.cancel"
      @clicked="onClickYesNoButton"
    />

    <edit-issue-popup @closed="onCloseNewIssuePopup"
                      @saved="onSaveNewIssuePopup"
                      :project-id="projectId"
                      :visible-popup="showNewIssuePopup">
    </edit-issue-popup>

  </v-dialog>

</template>

<script>

import { mapGetters } from 'vuex';
import moment from 'moment';

import { userRoleGroups } from '@/constants/auth';
import { getSecurityRiskCode, getSecurityRiskDescription } from '@/utils/stringUtils';
import { CLEAR_ISSUE_DETAILS } from '@/store/issue/mutationTypes';
import IssueProbability from '@/components/issue/IssueProbability.vue';
import IssueSeverity from '@/components/issue/IssueSeverity.vue';
import IssueRiskScore from '@/components/issue/IssueRiskScore.vue';
import IssueStatus from '@/components/issue/IssueStatus.vue';
import ChangeIssueStatusButton from '@/components/issue/ChangeIssueStatusButton.vue';
import { DELETE_ISSUE, LOAD_ISSUE_DETAILS } from '@/store/issue/actionTypes';
import EditDeleteIssueButton from '@/components/issue/EditDeleteIssueButton.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import EditIssuePopup from '@/components/popup/EditIssuePopup.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

const ISSUE_DETAILS_DATE_FORMAT = 'DD MMM YYYY hh:mm';

export default {
  name: 'IssueDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    EditDeleteIssueButton,
    IssueStatus,
    IssueProbability,
    IssueSeverity,
    IssueRiskScore,
    ChangeIssueStatusButton,
    YesNoPopup,
    EditIssuePopup,
  },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    showDeleteIssueConfirmationPopup: false,
    showNewIssuePopup: false,
  }),
  computed: {
    ...mapGetters(['issueDetails', 'userRoleGroup']),
    userGroups() {
      return userRoleGroups;
    },
    securityRiskCode() {
      return getSecurityRiskCode(this.issueDetails.securityRisk);
    },
    securityRiskDescription() {
      return getSecurityRiskDescription(this.issueDetails.securityRisk);
    },
    createdDate() {
      return moment(this.issueDetails.createdDate).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    updatedDate() {
      return moment(this.issueDetails.updatedDate).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    commentsTitle() {
      return this.userRoleGroup === userRoleGroups.SP
        ? 'issueDetailsPopup.mitigation' : 'issueDetailsPopup.comments';
    },
  },
  methods: {
    async onCloseClickButton() {
      await this.$store.dispatch(CLEAR_ISSUE_DETAILS);
      this.$emit('closed', true);
    },
    async issueStatusChangedHandler(data) {
      this.issueDetails.status = data.status;
    },
    async deleteIssueClickedHandler() {
      this.showDeleteIssueConfirmationPopup = true;
    },
    async editIssueClickedHandler() {
      this.showNewIssuePopup = true;
    },
    async onClickYesNoButton(result) {
      this.showDeleteIssueConfirmationPopup = false;
      if (result) {
        const options = { issueId: this.issueDetails.id, projectId: this.projectId };
        await this.$store.dispatch(DELETE_ISSUE, options);
        this.onCloseClickButton();
      }
    },
    async onCloseNewIssuePopup() {
      await this.$store.dispatch(LOAD_ISSUE_DETAILS, { issueId: this.issueDetails.id });
      this.showNewIssuePopup = false;
    },
    async onSaveNewIssuePopup() {
      await this.$store.dispatch(LOAD_ISSUE_DETAILS, { issueId: this.issueDetails.id });
      this.showNewIssuePopup = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@/assets/styles/design-system';

  .popup-container {
    width: rem(690);
    min-height: rem(447);

    border-radius: $radius-md;
    background-color: var(--bg-primary);
    box-shadow: $shadow-lg;

    top: rem(60);
    position: relative;
    margin: auto;

    .close {
      cursor: pointer;
      display: inline-block;
      position: absolute;
      right: rem(10);
      top: rem(10);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      color: var(--brand-primary);
    }

    .main-part {
      padding-left: rem(60);
      padding-top: rem(23);

      min-height: rem(239);
      border-radius: $radius-md $radius-md 0 0;
      background-color: var(--bg-secondary);
      box-shadow: $shadow-xs;
    }

    .rest-part {
      padding-left: rem(60);
      padding-top: rem(13);
    }

    .security-risk {

      margin-top: $space-2;
      display: inline-block;

      .risk-code {
        vertical-align: top;
        display: inline-block;
        color: var(--text-secondary);
        font-size: $font-size-xl;
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(24);

        overflow: hidden;
      }

      .security-risk-description {
        display: inline-block;
        margin-left: $space-3;
        text-transform: capitalize;

        color: var(--text-primary);
        font-size: $font-size-xl;
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(24);

        width: rem(350);
      }

    }

    .security-risk-data-title {
      display: inline-block;
    }

    .action-button-container {

      display: inline-block;
      float: right;

      &.mark-issue-as-fixed-button-container {
        margin-top: rem(11);
        margin-right: rem(27);
      }

      &.edit-delete-issue-button-container {
        margin-top: rem(7);
        margin-right: rem(60);
      }
    }

    .data-title {
      color: var(--text-secondary);
      font-size: $font-size-xs;
      letter-spacing: 0.8px;
      line-height: $font-size-xs;
      text-transform: uppercase;
      margin-bottom: $space-1-5;
    }

    .issue-status {
      text-align: left;
    }

    .description-block {

      margin-top: $space-4;
      display: block;

      .text-container {
        padding-right: rem(60);

        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
      }
    }

    .severity-risk-score-line {
      display: inline-block;
    }

    .issue-risk-score {
      margin-left: $space-2;
    }

    .date-column {
      color: var(--text-secondary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $font-size-base;
    }

    .comment-block {

      margin-top: rem(9);
      display: block;

      .text-container {
        padding-right: rem(60);

        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
        padding-bottom: rem(60);
      }
    }

    .data-row {
      margin-top: rem(25);
      padding-bottom: rem(35);

      .data-column {
        display: inline-block;
        width: rem(90);
        margin-right: $space-4;

        .probability {
          line-height: unset;
        }
      }

      .data-column-date {
        display: inline-block;
        width: rem(135);
        margin-right: $space-4;
      }
    }

  }

</style>
